import type { Cycling, Fast, FastsResponse, SavedMeal, SavedMealItem, Entitlement, FavouriteFood, MealSlot, TodayResponse, Goals, FoodEntry, Measurement, Activity, EstimateResponse, Drink, MeResponse, StatsResponse, StatsRange, WorkoutSet, WorkoutSetInput, AdminUser, CheckIn, GoalAdjustment, BarcodeLookupResponse, BarcodeRecentsResponse, NormalizedProduct, FrequentFood, ReminderSetting, ReminderPrefsResponse, PushSubscriptionInput, Micros, Sex, GoalType, HealthSyncDay,  } from "@shared/types";

import { API_BASE } from "./apiBase";
import { ensureTokenLoaded, getToken, setToken } from "./sessionToken";
import { enqueue } from "./offlineQueue";

/**
 * Create-style POST that survives being offline: the body is stamped with a
 * client_id (server-side dedupe key), and a NETWORK failure queues the write
 * for replay instead of failing — the caller gets `offline` back. Server
 * rejections (ApiError) still throw: the request is wrong, not the network.
 */
async function reqOrQueue<T>(path: string, body: Record<string, unknown>, offline: T): Promise<T> {
  const stamped = { client_id: crypto.randomUUID(), ...body };
  try {
    return await req<T>(path, { method: "POST", body: JSON.stringify(stamped) });
  } catch (e) {
    if (e instanceof ApiError) throw e;
    await enqueue({ path, method: "POST", body: stamped });
    return offline;
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  await ensureTokenLoaded();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string> ?? {}),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(API_BASE + path, {
    ...init,
    credentials: "include",
    headers,
  });
  // Login responses and rolling refreshes deliver the native bearer here.
  const fresh = res.headers.get("X-Session-Token");
  if (fresh) void setToken(fresh);
  if (res.status === 401) throw new ApiError(401, "unauthorized");
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, (body as { error?: string }).error ?? "request failed");
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

export const api = {
  logout: async () => {
    const r = await req<{ ok: true }>("/api/auth/logout", { method: "POST" });
    await setToken(null); // drop the native bearer alongside the cookie
    return r;
  },

  // Accounts (email/password + OAuth)
  register: (email: string, password: string, name: string | undefined, locale: "en" | "de") =>
    req<{ ok: true }>("/api/auth/register", { method: "POST", body: JSON.stringify({ email, password, name, locale }) }),
  loginEmail: (email: string, password: string) =>
    req<{ ok: true }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  forgotPassword: (email: string, locale: "en" | "de") =>
    req<{ ok: true }>("/api/auth/forgot", { method: "POST", body: JSON.stringify({ email, locale }) }),
  resetPassword: (token: string, new_password: string) =>
    req<{ ok: true }>("/api/auth/reset", { method: "POST", body: JSON.stringify({ token, new_password }) }),
  verifyEmail: (token: string) =>
    req<{ ok: true }>("/api/auth/verify", { method: "POST", body: JSON.stringify({ token }) }),
  resendVerify: (locale: "en" | "de") =>
    req<{ ok: true }>("/api/auth/verify/resend", { method: "POST", body: JSON.stringify({ locale }) }),
  verifyPassword: (current_password: string) =>
    req<{ ok: true }>("/api/auth/password", { method: "POST", body: JSON.stringify({ current_password }) }),
  changePassword: (current_password: string, new_password: string, locale: "en" | "de") =>
    req<{ ok: true }>("/api/auth/password", { method: "PUT", body: JSON.stringify({ current_password, new_password, locale }) }),
  deleteAccount: async () => {
    const r = await req<{ ok: true }>("/api/account", { method: "DELETE", body: JSON.stringify({ confirm: true }) });
    await setToken(null);
    return r;
  },
  redeemHandoff: (token: string, nonce?: string) =>
    req<{ ok: true }>("/api/auth/native/redeem", { method: "POST", body: JSON.stringify({ token, nonce }) }),
  nativeAppleLogin: (identity_token: string, name: string | undefined, locale: "en" | "de") =>
    req<{ ok: true; created: boolean }>("/api/auth/native/apple", { method: "POST", body: JSON.stringify({ identity_token, name, locale }) }),
  requestLoginCode: (email: string, locale: "en" | "de") =>
    req<{ ok: true }>("/api/auth/code/request", { method: "POST", body: JSON.stringify({ email, locale }) }),
  verifyLoginCode: (email: string, code: string) =>
    req<{ ok: true; created: boolean }>("/api/auth/code/verify", { method: "POST", body: JSON.stringify({ email, code }) }),
  me: () => req<MeResponse>("/api/auth/me"),
  authProviders: () => req<{ providers: ("apple" | "google")[] }>("/api/auth/providers"),

  onboarding: (payload: {
    name?: string; sex: Sex; age: number; height_cm: number; weight_kg: number;
    target_weight_kg: number; goal_type: GoalType;
    daily_calories?: number; protein_g?: number; measured_on: string;
    event_name?: string; event_date?: string;
    birthday?: string; nutrition_pref?: string;
    activity?: "sedentary" | "light" | "moderate" | "high"; referral_source?: string; tried_tracking?: boolean;
  }) => req<{ ok: true; daily_calories: number; maintenance_calories: number }>(
    "/api/onboarding", { method: "POST", body: JSON.stringify(payload) }),
  onboardingSkip: () => req<{ ok: true }>("/api/onboarding", { method: "POST", body: JSON.stringify({ skip: true }) }),

  today:  (date: string, locale: "en" | "de" = "en") => req<TodayResponse>(`/api/today?date=${date}&locale=${locale}`),

  listFood:  (date: string)          => req<FoodEntry[]>(`/api/food?date=${date}`),
  addFood:   (entry: Omit<FoodEntry, "id" | "eaten_at" | "protein_g" | "carbs_g" | "fat_g" | "sugar_g" | "fiber_g" | "micros"> & { eaten_at?: number; protein_g?: number | null; carbs_g?: number | null; fat_g?: number | null; sugar_g?: number | null; fiber_g?: number | null; micros?: Micros | null; client_id?: string }) =>
                                        reqOrQueue<{ id: number }>("/api/food", entry as unknown as Record<string, unknown>, { id: -1 }),
  deleteFood:(id: number)            => req<{ ok: true }>(`/api/food/${id}`, { method: "DELETE" }),
  copyDay: (from_date: string, to_date: string, slot?: MealSlot) =>
    reqOrQueue<{ ids: number[]; copied: number }>(
      "/api/food/copy", { from_date, to_date, slot }, { ids: [], copied: 0 }),
  favourites: () => req<FavouriteFood[]>("/api/food/favourites"),
  setFavourite: (name: string, starred: boolean) =>
    req<{ ok: true }>("/api/food/favourites", { method: "PUT", body: JSON.stringify({ name, starred }) }),
  frequentFoods: (sort: "count" | "recent" | "smart" = "count", limit = 20, slot?: MealSlot) =>
    req<FrequentFood[]>(`/api/food/frequent?sort=${sort}&limit=${limit}${slot ? `&slot=${slot}` : ""}`),

  listMeasurements:(from: string, to: string) =>
    req<Measurement[]>(`/api/measurements?from=${from}&to=${to}`),
  // Measurements POST is a natural upsert on (user, date) — no client_id
  // needed, but network failures still queue for replay. Logged weights are
  // announced so the Apple Health mirror (lib/health.ts) can pick them up.
  upsertMeasurement: async (m: { measured_on: string } & Partial<Omit<Measurement, "id" | "measured_on">>) => {
    const r = await reqOrQueue<{ ok: true }>("/api/measurements", m as unknown as Record<string, unknown>, { ok: true });
    if (typeof m.weight_kg === "number" && m.weight_kg > 0) {
      window.dispatchEvent(new CustomEvent("caloly:weight-logged", {
        detail: { weight_kg: m.weight_kg, measured_on: m.measured_on },
      }));
    }
    return r;
  },

  getGoals: ()                  => req<Goals>("/api/goals"),
  putGoals: (g: Partial<Goals>) => req<{ ok: true }>("/api/goals", { method: "PUT", body: JSON.stringify(g) }),
  putProfile: (p: {
    name?: string; last_name?: string | null; username?: string | null; city?: string | null;
    unit_prefs?: import("@shared/units").UnitPrefs;
    birthday?: string | null; sex?: import("@shared/types").Sex | null;
    height_cm?: number | null; nutrition_pref?: string | null;
  }) => req<{ ok: true }>("/api/profile", { method: "PUT", body: JSON.stringify(p) }),

  // Activities
  listActivities: (date: string) =>
    req<Activity[]>(`/api/activities?date=${date}`),
  addActivity: (a: Omit<Activity, "id" | "source">) => // server defaults source to manual
    reqOrQueue<{ id: number }>("/api/activities", a as unknown as Record<string, unknown>, { id: -1 }),
  myActivities: () => req<Array<{ name: string; emoji: string | null; met: number }>>("/api/activities/mine"),
  updateActivity: (id: number, patch: Partial<Activity>) =>
    req<{ ok: true }>(`/api/activities/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteActivity: (id: number) =>
    req<{ ok: true }>(`/api/activities/${id}`, { method: "DELETE" }),

  // Food edit
  updateFood: (id: number, patch: Partial<FoodEntry>) =>
    req<{ ok: true }>(`/api/food/${id}`, { method: "PUT", body: JSON.stringify(patch) }),

  // Measurement delete
  deleteMeasurement: (date: string) =>
    req<{ ok: true }>(`/api/measurements/${date}`, { method: "DELETE" }),

  estimateFood: (description: string) =>
    req<EstimateResponse>("/api/estimate", {
      method: "POST",
      body: JSON.stringify({ description }),
    }),

  estimatePhoto: (image_b64: string, description?: string) =>
    req<EstimateResponse>("/api/estimate-photo", {
      method: "POST",
      body: JSON.stringify({ image_b64, description }),
    }),

  // Drinks
  listDrinks: (date: string) =>
    req<Drink[]>(`/api/drinks?date=${date}`),
  addDrink: (d: Omit<Drink, "id">) =>
    reqOrQueue<{ id: number }>("/api/drinks", d as unknown as Record<string, unknown>, { id: -1 }),
  updateDrink: (id: number, patch: Partial<Drink>) =>
    req<{ ok: true }>(`/api/drinks/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteDrink: (id: number) =>
    req<{ ok: true }>(`/api/drinks/${id}`, { method: "DELETE" }),

  // User management
  listUsers: () =>
    req<AdminUser[]>("/api/users"),
  /** Grant (period) or revoke (null) complimentary premium for a user. */
  adminComp: (user_id: number, period: "monthly" | "yearly" | null) =>
    req<{ ok: true; comp_premium_until: number | null; comp_premium_period: string | null }>(
      "/api/admin/comp", { method: "POST", body: JSON.stringify({ user_id, period }) }),
  updateUser: (id: number, patch: { name?: string }) =>
    req<{ ok: true }>(`/api/users/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteUser: (id: number) =>
    req<{ ok: true }>(`/api/users/${id}`, { method: "DELETE" }),
  savedMeals: () => req<SavedMeal[]>("/api/food/saved-meals"),
  updateSavedMeal: (id: number, patch: { name?: string; items?: Array<Omit<SavedMealItem, "id">> }) =>
    req<{ ok: true }>(`/api/food/saved-meals/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  createSavedMeal: (name: string, emoji: string | null, items: Array<Omit<SavedMealItem, "id">>) =>
    req<{ id: number }>("/api/food/saved-meals", { method: "POST", body: JSON.stringify({ name, emoji, items, client_id: crypto.randomUUID() }) }),
  deleteSavedMeal: (id: number) =>
    req<{ ok: true }>(`/api/food/saved-meals/${id}`, { method: "DELETE" }),
  estimateVoice: (audio_b64: string, mime: string) =>
    req<EstimateResponse & { transcript: string }>("/api/estimate-voice", { method: "POST", body: JSON.stringify({ audio_b64, mime }) }),
  cycling: () => req<Cycling>("/api/cycling"),
  setCycling: (c: Cycling) => req<Cycling>("/api/cycling", { method: "PUT", body: JSON.stringify(c) }),
  fasts: (today: string) => req<FastsResponse>(`/api/fasts?today=${today}`),
  startFast: (protocol: Fast["protocol"]) =>
    req<Fast>("/api/fasts", { method: "POST", body: JSON.stringify({ protocol, client_id: crypto.randomUUID() }) }),
  endFast: () => req<Fast>("/api/fasts", { method: "DELETE" }),
  exportData: async (format: "json" | "csv"): Promise<Blob> => {
    await ensureTokenLoaded();
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/api/export?format=${format}`, {
      credentials: "include",
      headers,
    });
    if (!res.ok) throw new ApiError(res.status, "export_failed");
    return res.blob();
  },
  aiCorrectionPut: (name: string, m: { calories: number; protein_g?: number | null; carbs_g?: number | null; fat_g?: number | null; sugar_g?: number | null }) =>
    req<{ ok: true }>("/api/ai-corrections", { method: "POST", body: JSON.stringify({ name, ...m }) }),
  entitlement: () => req<Entitlement>("/api/entitlement"),
  stripeCheckout: (plan: "monthly" | "yearly") =>
    req<{ url: string }>("/api/subscription/stripe-checkout", { method: "POST", body: JSON.stringify({ plan }) }),
  stripePortal: () =>
    req<{ url: string }>("/api/subscription/stripe-portal", { method: "POST" }),
  adminStats: () =>
    req<{ members: number; active_7d: number; food_entries: number; weigh_ins: number }>("/api/admin/stats"),
  healthSync: (days: HealthSyncDay[]) =>
    req<{ ok: true; synced: number }>("/api/health/sync", { method: "POST", body: JSON.stringify({ days }) }),

  getStats: (range: StatsRange, today: string): Promise<StatsResponse> =>
    req<StatsResponse>(`/api/stats?range=${range}&today=${today}`),

  // Workouts
  listWorkouts: (on: string): Promise<WorkoutSet[]> =>
    req<WorkoutSet[]>(`/api/workouts?on=${on}`),
  addWorkouts: (payload: ({ performed_on?: string; sets?: WorkoutSetInput[] } | (WorkoutSetInput & { performed_on?: string }))): Promise<WorkoutSet[]> =>
    reqOrQueue<WorkoutSet[]>("/api/workouts", payload as unknown as Record<string, unknown>, []),
  updateWorkout: (id: number, patch: Partial<WorkoutSetInput>): Promise<WorkoutSet> =>
    req<WorkoutSet>(`/api/workouts/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteWorkout: (id: number): Promise<void> =>
    req<void>(`/api/workouts/${id}`, { method: "DELETE" }),
  deleteWorkoutByExercise: (on: string, exercise: string): Promise<{ deleted: number }> =>
    req<{ deleted: number }>(`/api/workouts/by-exercise?on=${on}&exercise=${encodeURIComponent(exercise)}`, { method: "DELETE" }),

  // Goal adjustments
  listGoalAdjustments: (limit = 20) =>
    req<GoalAdjustment[]>(`/api/goals/adjustments?limit=${limit}`),
  recalibrate: (today: string, dryRun = false) =>
    req<GoalAdjustment | null>(`/api/goals/recalibrate?today=${today}&dry_run=${dryRun}`, { method: "POST" }),

  // Barcode
  barcodeLookup: (code: string) =>
    req<BarcodeLookupResponse>(`/api/barcode/${encodeURIComponent(code)}`),
  barcodeRecents: () =>
    req<BarcodeRecentsResponse>("/api/barcode/recents"),
  barcodeRecordRecent: (code: string) =>
    req<{ ok: true }>("/api/barcode/recents", {
      method: "POST", body: JSON.stringify({ code }),
    }),
  // Write a browser-fetched OFF result back into the shared server cache.
  // `product === null` records a negative (miss) so other users skip the fetch.
  barcodeCachePut: (code: string, product: NormalizedProduct | null) =>
    req<{ ok: true }>(`/api/barcode/${encodeURIComponent(code)}`, {
      method: "POST",
      body: JSON.stringify(product ? { product } : { miss: true }),
    }),

  // Check-ins
  listCheckIns: () =>
    req<CheckIn[]>("/api/check-ins"),
  generateCheckIn: (locale: "en" | "de", today: string) =>
    req<CheckIn>(`/api/check-ins/generate?locale=${locale}`, {
      method: "POST", body: JSON.stringify({ today }),
    }),
  actionCheckIn: (id: number, user_action: "applied" | "dismissed") =>
    req<CheckIn>(`/api/check-ins/${id}`, {
      method: "PUT", body: JSON.stringify({ user_action }),
    }),

  // Push notifications
  pushKey: () => req<{ key: string }>("/api/push/key"),
  pushSubscribe: (sub: PushSubscriptionInput) =>
    req<{ ok: true }>("/api/push/subscribe", { method: "POST", body: JSON.stringify(sub) }),
  pushUnsubscribe: (endpoint: string) =>
    req<{ ok: true }>("/api/push/subscribe", { method: "DELETE", body: JSON.stringify({ endpoint }) }),
  askAssistant: (question: string, today?: string) =>
    req<{ answer?: string; safety_key?: string; pose?: string }>("/api/assistant", {
      method: "POST", body: JSON.stringify({ question, today }),
    }),
  /** TEMPORARY admin account switcher. */
  adminSwitch: (email: string) =>
    req<{ ok: true; return_ticket: string }>("/api/admin/switch", { method: "POST", body: JSON.stringify({ email }) }),
  switchBack: (return_ticket: string) =>
    req<{ ok: true }>("/api/auth/switch-back", { method: "POST", body: JSON.stringify({ return_ticket }) }),
  adminResetOnboarding: (email: string) =>
    req<{ ok: true }>("/api/admin/reset-onboarding", { method: "POST", body: JSON.stringify({ email }) }),
  getReminders: () => req<ReminderPrefsResponse>("/api/reminders"),
  putReminder: (s: ReminderSetting) =>
    req<{ ok: true }>("/api/reminders", { method: "PUT", body: JSON.stringify(s) }),
  /** Master switch and quiet hours. Any subset of the three. */
  putReminderPrefs: (p: { master?: boolean; quiet_from?: string; quiet_to?: string }) =>
    req<{ ok: true }>("/api/reminders", { method: "PUT", body: JSON.stringify(p) }),
};
